import { useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  ShieldCheck, SignOut, User, Ruler, Key, Buildings,
  EnvelopeSimple, List as MenuIcon, Lightbulb,
} from "@phosphor-icons/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { updatePreferences } from "@/lib/api";
import BrandHeader from "@/components/BrandHeader";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import PasskeyPrompt from "@/lib/PasskeyPrompt";
import StageTracker from "@/components/StageTracker";
import { SIDEBAR_ITEMS, resolveActiveSidebar } from "@/config/simplified_flow";

/**
 * Reusable icon-link. In Feb 2026 the sidebar always passes `active`
 * explicitly (single source of truth = resolveActiveSidebar), so we
 * short-circuit NavLink's own isActive logic. This prevents the
 * "two blue icons" bug where NavLink would highlight two nested
 * routes at once.
 */
function NavItem({ to, icon: Icon, label, testid, variant = "rail", onClick, active = false }) {
  if (variant === "drawer") {
    return (
      <Link
        to={to}
        onClick={onClick}
        className={
          `flex items-center gap-3 h-11 px-4 rounded-lg text-[13.5px] font-medium transition-colors ${
            active
              ? "bg-brand-blue text-white"
              : "text-slate-700 hover:bg-slate-100 active:bg-slate-200"
          }`
        }
        data-testid={testid}
        aria-current={active ? "page" : undefined}
      >
        <Icon size={18} weight="regular" />
        {label}
      </Link>
    );
  }
  const railClass =
    `w-10 h-10 rounded-md flex items-center justify-center transition-colors ${
      active
        ? "bg-brand-blue text-white shadow-[0_2px_8px_-2px_rgba(30,111,255,0.55)]"
        : "text-slate-500 hover:bg-slate-100 hover:text-brand-blue"
    }`;
  return (
    <Link
      to={to}
      title={label}
      className={railClass}
      data-testid={testid}
      aria-current={active ? "page" : undefined}
    >
      <Icon size={18} />
    </Link>
  );
}

export default function AppShell() {
  const { pathname } = useLocation();
  const { user, logout, setUser } = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const unitMut = useMutation({
    mutationFn: (unit_pref) => updatePreferences({ unit_pref }),
    onSuccess: (_r, unit_pref) => {
      setUser({ ...user, unit_pref });
      qc.invalidateQueries();
      toast.success(`Units: ${unit_pref === "imperial" ? "Imperial (ft, st)" : "Metric (m, t)"}`);
    },
  });

  // Feb 2026 · Client mandate — sidebar shows ONLY 4 canonical items
  // and exactly ONE is ever active (resolved by exact-path rules).
  const activeKey = resolveActiveSidebar(pathname);
  const visibleSidebarItems = SIDEBAR_ITEMS.filter(
    (i) => !i.requiresRole || i.requiresRole === user?.role
  );

  return (
    <div
      className="flex h-[100dvh] w-screen overflow-hidden bg-white text-slate-900"
      data-testid="app-shell"
    >
      {/* ───────── Desktop / tablet-landscape sidebar (≥md) ───────── */}
      <aside
        className="hidden md:flex w-16 shrink-0 bg-white border-r border-slate-200 flex-col items-center py-4 gap-2 overflow-y-auto"
        data-testid="sidebar"
      >
        <Link
          to="/"
          className="w-11 h-11 rounded-md flex items-center justify-center hover:bg-slate-100 transition-colors"
          data-testid="logo-link"
          title="SRES — back to landing page"
        >
          <img src="/sres-mark.png?v=6" alt="SRES" className="h-7 w-7 object-contain" />
        </Link>
        <div className="h-px w-8 bg-slate-200 my-1" />
        {visibleSidebarItems.map((n) => (
          <NavItem
            key={n.key}
            to={n.to}
            icon={n.icon}
            label={n.label}
            testid={n.testid}
            active={activeKey === n.key}
          />
        ))}
        <div className="flex-1" />
        <div className="text-[9px] text-slate-400 tabular tracking-widest">v0.3</div>
      </aside>

      {/* ───────── Main pane ───────── */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <BrandHeader
          brandTo="/"
          navLinks={[]}
          leftSlot={
            /* Hamburger — mobile / tablet-portrait only */
            <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
              <SheetTrigger asChild>
                <button
                  aria-label="Open navigation menu"
                  className="md:hidden inline-flex items-center justify-center w-11 h-11 -ml-2 rounded-md text-slate-700 hover:bg-slate-100 active:bg-slate-200 transition-colors"
                  data-testid="mobile-menu-trigger"
                >
                  <MenuIcon size={22} weight="regular" />
                </button>
              </SheetTrigger>
              <SheetContent
                side="left"
                className="w-[280px] max-w-[85vw] bg-white p-0 flex flex-col border-r border-slate-200"
                data-testid="mobile-drawer"
              >
                <div className="px-5 pt-6 pb-5 border-b border-slate-100">
                  <Link
                    to="/"
                    onClick={() => setDrawerOpen(false)}
                    className="inline-flex items-center gap-2"
                    data-testid="mobile-drawer-brand"
                  >
                    <img src="/sres-logo-horizontal.png?v=5" alt="SRES" className="h-9 w-auto object-contain" />
                  </Link>
                  <div className="mt-4 flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-md bg-brand-blue/10 border border-brand-blue/25 flex items-center justify-center">
                      <User size={14} weight="bold" className="text-brand-blue" />
                    </div>
                    <div className="leading-tight min-w-0">
                      <div className="text-[13px] font-semibold text-slate-900 truncate">
                        {user?.name || user?.email}
                      </div>
                      <div className="text-[10px] text-slate-500 uppercase tracking-[0.2em] font-mono">
                        {user?.role}
                      </div>
                    </div>
                  </div>
                </div>

                <nav className="flex-1 overflow-y-auto p-3 space-y-1" data-testid="mobile-nav-list">
                  {visibleSidebarItems.map((n) => (
                    <NavItem
                      key={n.key}
                      to={n.to}
                      icon={n.icon}
                      label={n.label}
                      testid={n.testid}
                      variant="drawer"
                      active={activeKey === n.key}
                      onClick={() => setDrawerOpen(false)}
                    />
                  ))}
                </nav>

                {/* Unit toggle inside drawer, mobile-only */}
                <div className="px-4 pb-3">
                  <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500 font-mono mb-2">
                    Units
                  </div>
                  <div className="flex items-center h-11 bg-slate-100 rounded-lg p-1" data-testid="unit-toggle-mobile">
                    {["metric", "imperial"].map((u) => {
                      const active = (user?.unit_pref || "metric") === u;
                      return (
                        <button
                          key={u}
                          onClick={() => !active && unitMut.mutate(u)}
                          className={`flex-1 inline-flex items-center justify-center h-9 px-3 rounded-md text-[11px] font-semibold uppercase tracking-[0.16em] transition-all ${
                            active ? "bg-white text-brand-blue shadow-sm" : "text-slate-500 hover:text-slate-800"
                          }`}
                          data-testid={`unit-mobile-${u}`}
                        >
                          <Ruler size={12} weight="bold" className="mr-1.5" /> {u}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="border-t border-slate-100 p-3 space-y-1">
                  <button
                    onClick={() => { setDrawerOpen(false); nav("/change-password"); }}
                    className="w-full flex items-center gap-3 h-12 px-4 rounded-lg text-[14.5px] font-medium text-slate-700 hover:bg-slate-100 active:bg-slate-200 transition-colors"
                    data-testid="mobile-menu-change-password"
                  >
                    <Key size={20} weight="regular" /> Change password
                  </button>
                  {user?.role === "superadmin" && (
                    <button
                      onClick={() => { setDrawerOpen(false); nav("/app/admin/inbox"); }}
                      className="w-full flex items-center gap-3 h-12 px-4 rounded-lg text-[14.5px] font-medium text-slate-700 hover:bg-slate-100 active:bg-slate-200 transition-colors"
                      data-testid="mobile-menu-inbox"
                    >
                      <EnvelopeSimple size={20} weight="regular" /> Marketing inbox
                    </button>
                  )}
                  {user?.role === "admin" && (
                    <button
                      onClick={() => { setDrawerOpen(false); nav("/app/company-admin"); }}
                      className="w-full flex items-center gap-3 h-12 px-4 rounded-lg text-[14.5px] font-medium text-slate-700 hover:bg-slate-100 active:bg-slate-200 transition-colors"
                      data-testid="mobile-menu-company-admin"
                    >
                      <Buildings size={20} weight="regular" /> Company admin
                    </button>
                  )}
                  <button
                    onClick={() => { setDrawerOpen(false); logout(); nav("/login", { replace: true }); }}
                    className="w-full flex items-center gap-3 h-12 px-4 rounded-lg text-[14.5px] font-medium text-red-600 hover:bg-red-50 active:bg-red-100 transition-colors"
                    data-testid="mobile-menu-logout"
                  >
                    <SignOut size={20} weight="regular" /> Sign out
                  </button>
                </div>
              </SheetContent>
            </Sheet>
          }
          rightSlot={
            <>
              {/* Unit toggle — desktop only (mobile version lives in drawer) */}
              <div
                className="hidden md:flex items-center h-9 bg-slate-100 rounded-lg p-0.5"
                data-testid="unit-toggle"
              >
                {["metric", "imperial"].map((u) => {
                  const active = (user?.unit_pref || "metric") === u;
                  return (
                    <button
                      key={u}
                      onClick={() => !active && unitMut.mutate(u)}
                      className={`inline-flex items-center h-8 px-3 rounded-md text-[10.5px] font-semibold uppercase tracking-[0.16em] transition-all ${
                        active
                          ? "bg-white text-brand-blue shadow-sm"
                          : "text-slate-500 hover:text-slate-800"
                      }`}
                      data-testid={`unit-${u}`}
                    >
                      <Ruler size={11} weight="bold" className="mr-1.5" /> {u}
                    </button>
                  );
                })}
              </div>

              {/* Vertical divider */}
              <div className="hidden md:block w-px h-6 bg-slate-200" />

              {/* User menu — collapses to just the avatar on mobile */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="flex items-center gap-2.5 h-10 md:h-9 pl-1 pr-1 md:pr-2 rounded-lg hover:bg-slate-100 transition-colors"
                    data-testid="user-menu-trigger"
                  >
                    <div className="w-8 h-8 md:w-7 md:h-7 rounded-md bg-brand-blue/10 border border-brand-blue/25 flex items-center justify-center">
                      <User size={14} weight="bold" className="text-brand-blue" />
                    </div>
                    <div className="text-left leading-tight hidden lg:block pr-1">
                      <div className="text-[12px] font-semibold text-slate-900 max-w-[160px] truncate">
                        {user?.name || user?.email}
                      </div>
                      <div className="text-[9.5px] text-slate-500 uppercase tracking-[0.2em] font-mono">
                        {user?.role}
                      </div>
                    </div>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="bg-white border-slate-200 rounded-lg min-w-56 text-slate-800 shadow-xl"
                >
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">
                    {user?.email}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-slate-100" />
                  <DropdownMenuItem className="cursor-pointer" onSelect={() => nav("/change-password")} data-testid="menu-change-password">
                    <Key size={14} className="mr-2 text-slate-500" /> Change password
                  </DropdownMenuItem>
                  {/* Iter114 · Feb 2026 · Replay the cinema-mode onboarding
                      coach for returning users. Clears the shown flag +
                      hard-reloads so the coach anchors correctly against
                      the freshly-painted header. */}
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onSelect={() => {
                      try { localStorage.removeItem("sres.cinema-onboarding-shown"); }
                      catch { /* silent */ }
                      toast.success("Tips re-enabled — refreshing to replay the coach…");
                      setTimeout(() => window.location.reload(), 600);
                    }}
                    data-testid="menu-show-tips-again"
                  >
                    <Lightbulb size={14} className="mr-2 text-amber-500" /> Show tips again
                  </DropdownMenuItem>
                  {user?.role === "superadmin" && (
                    <>
                      <DropdownMenuItem className="cursor-pointer" onSelect={() => nav("/app/superadmin")} data-testid="menu-superadmin">
                        <ShieldCheck size={14} className="mr-2 text-brand-blue" /> Super Admin Console
                      </DropdownMenuItem>
                      <DropdownMenuItem className="cursor-pointer" onSelect={() => nav("/app/admin/inbox")} data-testid="menu-inbox">
                        <EnvelopeSimple size={14} className="mr-2 text-brand-blue" /> Marketing inbox
                      </DropdownMenuItem>
                    </>
                  )}
                  {user?.role === "admin" && (
                    <DropdownMenuItem className="cursor-pointer" onSelect={() => nav("/app/company-admin")} data-testid="menu-company-admin">
                      <Buildings size={14} className="mr-2 text-brand-blue" /> Company admin
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="bg-slate-100" />
                  <DropdownMenuItem className="cursor-pointer text-red-600" onSelect={() => { logout(); nav("/login", { replace: true }); }} data-testid="menu-logout">
                    <SignOut size={14} className="mr-2" /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          }
        />
        <div className="flex-1 overflow-hidden bg-white min-w-0" data-testid="app-outlet-host">
          <Outlet />
        </div>
      </main>
      <PasskeyPrompt />
      {/* Iter82 · Feb 2026 · silent per-project stage-time tracker
          — powers the "You: X min ✓" chips in the wizard sidebar. */}
      <StageTracker />
    </div>
  );
}
